import { slicedToArray as _slicedToArray, extends as _extends, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { useCallbackRef, useIsomorphicEffect } from '@costar-ui/hooks';
import { useModalContext } from '@costar-ui/share-context';
import { useObjectRef, useViewportSize, mergeProps, filterDOMProps } from '@react-aria/utils';
import React__default, { forwardRef, useContext, useRef, useState, useEffect, useMemo } from 'react';
import { useIsSSR } from 'react-aria';
import { useContextProps, Provider } from 'react-aria-components';
import { useOverlayTriggerState } from 'react-stately';
import { ModalContext, InternalModalContext } from './context.esm.js';
import Overlay from '../../../../aria/overlay/overlay.esm.js';
import useModalOverlay from '../../../../aria/overlay/use-modal-overlay.esm.js';
import { OverlayTriggerStateContext } from '../../../dialog/primitives/aria/context.esm.js';
import { useExitAnimation, useEnterAnimation, useRenderProps } from '../../../utils/index.esm.js';

var _excluded = ["testId", "UNSTABLE_portalContainer"];
function ModalOverlayInner(_ref) {
  var testId = _ref.testId,
    UNSTABLE_portalContainer = _ref.UNSTABLE_portalContainer,
    props = _objectWithoutProperties(_ref, _excluded);
  var modal = useRef({});
  var _ref2 = useModalContext() || {},
    manager = _ref2.manager;
  var mountNodeRef = useRef();
  var modalRef = props.modalRef;
  var state = props.state;
  var _useModalOverlay = useModalOverlay(props, state,
    // @ts-ignore
    modalRef),
    modalProps = _useModalOverlay.modalProps,
    underlayProps = _useModalOverlay.underlayProps;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    mountNode = _useState2[0],
    setMountNode = _useState2[1];
  var getModal = useCallbackRef(function () {
    modal.current.modalRef = modalRef.current;
    modal.current.mountNode = mountNodeRef.current;
    return modal.current;
  });
  var handleMounted = useCallbackRef(function () {
    manager === null || manager === void 0 || manager.mount(getModal());
    if (modalRef.current) {
      // Fix a bug on Chrome where the scroll isn't initially 0.
      modalRef.current.scrollTop = 0;
    }
  });
  var handleOpen = useCallbackRef(function () {
    var resolvedContainer = mountNodeRef.current || document.body;
    manager === null || manager === void 0 || manager.add(getModal(), resolvedContainer);

    // The element was already mounted.
    if (modalRef.current) {
      handleMounted();
    }
  });
  var handleClose = useCallbackRef(function () {
    manager === null || manager === void 0 || manager.remove(getModal());
  });
  useEffect(function () {
    return function () {
      handleClose();
    };
  }, [handleClose]);
  useEffect(function () {
    if (state.isOpen) {
      handleOpen();
    } else {
      handleClose();
    }
  }, [handleClose, handleOpen, state.isOpen]);
  useIsomorphicEffect(function () {
    setMountNode(UNSTABLE_portalContainer || document.body);
  }, [UNSTABLE_portalContainer]);
  useIsomorphicEffect(function () {
    mountNodeRef.current = mountNode !== null && mountNode !== void 0 ? mountNode : undefined;
  }, [mountNode]);
  var entering = useEnterAnimation(props.overlayRef) || props.isEntering || false;
  var renderProps = useRenderProps(_objectSpread2(_objectSpread2({}, props), {}, {
    defaultClassName: 'react-aria-ModalOverlay',
    values: {
      isEntering: entering,
      isExiting: props.isExiting,
      state: state
    }
  }));
  var viewport = useViewportSize();
  var style = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, renderProps.style), {}, {
      '--visual-viewport-height': viewport.height + 'px'
    });
  }, [renderProps.style, viewport.height]);
  return /*#__PURE__*/React__default.createElement(Overlay, {
    isExiting: props.isExiting,
    portalContainer: UNSTABLE_portalContainer
  }, /*#__PURE__*/React__default.createElement("div", _extends({
    "data-testid": testId
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, mergeProps(filterDOMProps(props), underlayProps), renderProps, {
    style: style,
    ref: props.overlayRef,
    "data-entering": entering || undefined,
    "data-exiting": props.isExiting || undefined
  }), /*#__PURE__*/React__default.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [[InternalModalContext, {
      modalProps: modalProps,
      modalRef: modalRef,
      isExiting: props.isExiting,
      isDismissable: props.isDismissable
    }], [OverlayTriggerStateContext, state]]
  }, renderProps.children)));
}
function ModalOverlayWithForwardRef(props, ref) {
  var _useContextProps = useContextProps(props, ref, ModalContext);
  var _useContextProps2 = _slicedToArray(_useContextProps, 2);
  props = _useContextProps2[0];
  ref = _useContextProps2[1];
  var contextState = useContext(OverlayTriggerStateContext);
  var localState = useOverlayTriggerState(props);
  var state = props.isOpen != null || props.defaultOpen != null || !contextState ? localState : contextState;
  var objectRef = useObjectRef(ref);
  var modalRef = useRef(null);
  var isOverlayExiting = useExitAnimation(objectRef, state.isOpen);
  var isModalExiting = useExitAnimation(modalRef, state.isOpen);
  var isExiting = isOverlayExiting || isModalExiting || props.isExiting || false;
  var isSSR = useIsSSR();
  if (!state.isOpen && !isExiting || isSSR) {
    return null;
  }
  return /*#__PURE__*/React__default.createElement(ModalOverlayInner, _extends({}, props, {
    state: state,
    isExiting: isExiting,
    overlayRef: objectRef,
    modalRef: modalRef
  }));
}

/**
 * A ModalOverlay is a wrapper for a Modal which allows customizing the backdrop element.
 */
var ModalOverlay = /*#__PURE__*/forwardRef(ModalOverlayWithForwardRef);
var ModalOverlay$1 = ModalOverlay;

export { ModalOverlay$1 as default };
//# sourceMappingURL=modal-overlay.esm.js.map
